import { useState, useRef, useEffect } from 'react';
import { CreditCard, Mail, ChevronDown } from 'lucide-react';
import Card from '@/components/ui/Card';
import Badge from '@/components/ui/Badge';
import Button from '@/components/ui/Button';
import { formatCurrency } from '@/lib/utils';
import type { Order } from './types';

interface PaymentCardProps {
  order: Order;
  onApplyDiscount: () => void;
  onRefund: () => void;
  onSendInvoice: () => void;
  onResendEmail: (type: 'confirmation' | 'shipping' | 'review') => void;
}

const paymentBadgeVariant = (ps: string): 'green' | 'yellow' | 'red' | 'gray' => {
  switch (ps) {
    case 'paid': return 'green';
    case 'pending': return 'yellow';
    case 'failed': case 'refunded': return 'red';
    default: return 'gray';
  }
};

export default function PaymentCard({ order, onApplyDiscount, onRefund, onSendInvoice, onResendEmail }: PaymentCardProps) {
  const [showEmailDropdown, setShowEmailDropdown] = useState(false);
  const emailDropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (emailDropdownRef.current && !emailDropdownRef.current.contains(e.target as Node)) {
        setShowEmailDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const canRefund = order.payment_status === 'paid' && order.status !== 'refunded';

  return (
    <Card title="Payment" padding="md">
      <div className="space-y-4">
        {/* Payment method + status */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm text-stone-700">
            <CreditCard className="h-4 w-4 text-stone-400" />
            <span className="capitalize">{order.payment_method}</span>
          </div>
          <Badge variant={paymentBadgeVariant(order.payment_status)}>
            {order.payment_status.charAt(0).toUpperCase() + order.payment_status.slice(1)}
          </Badge>
        </div>

        {/* Totals breakdown */}
        <div className="space-y-2 text-sm border-t border-stone-100 pt-3">
          <div className="flex justify-between">
            <span className="text-stone-500">Subtotal</span>
            <span className="text-stone-700">{formatCurrency(order.subtotal)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-stone-500">Shipping</span>
            <span className="text-stone-700">{formatCurrency(order.shipping_cost)}</span>
          </div>
          {order.tax_amount > 0 && (
            <div className="flex justify-between">
              <span className="text-stone-500">Tax</span>
              <span className="text-stone-700">{formatCurrency(order.tax_amount)}</span>
            </div>
          )}
          {order.discount_amount > 0 && (
            <div className="flex justify-between">
              <span className="text-stone-500">Discount</span>
              <span className="text-red-600">-{formatCurrency(order.discount_amount)}</span>
            </div>
          )}
          <div className="flex justify-between border-t border-stone-100 pt-2">
            <span className="font-semibold text-stone-900">Total</span>
            <span className="font-semibold text-stone-900">{formatCurrency(order.total)}</span>
          </div>
        </div>

        {/* Coupon code */}
        {order.coupon_code && (
          <div className="text-sm">
            <span className="text-stone-500">Coupon:</span>{' '}
            <span className="font-mono text-stone-700 bg-stone-50 px-1.5 py-0.5 rounded">{order.coupon_code}</span>
          </div>
        )}

        {/* Shipping method */}
        {order.shipping_method && (
          <div className="text-sm">
            <span className="text-stone-500">Shipping method:</span>{' '}
            <span className="text-stone-700">{order.shipping_method}</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex flex-wrap gap-2 border-t border-stone-100 pt-3">
          <Button variant="ghost" size="sm" onClick={onApplyDiscount}>
            Apply Discount
          </Button>
          <Button variant="ghost" size="sm" onClick={onSendInvoice}>
            Send Updated Invoice
          </Button>

          {/* Email dropdown */}
          <div className="relative" ref={emailDropdownRef}>
            <Button variant="ghost" size="sm" onClick={() => setShowEmailDropdown(!showEmailDropdown)}>
              <Mail className="h-4 w-4 mr-1" />
              Send Email
              <ChevronDown className="h-3 w-3 ml-1" />
            </Button>

            {showEmailDropdown && (
              <div className="absolute left-0 top-full mt-1 z-20 w-48 rounded-lg border border-stone-200 bg-white shadow-lg py-1">
                <button
                  className="flex w-full px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
                  onClick={() => { onResendEmail('confirmation'); setShowEmailDropdown(false); }}
                >
                  Order Confirmation
                </button>
                <button
                  className="flex w-full px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
                  onClick={() => { onResendEmail('shipping'); setShowEmailDropdown(false); }}
                >
                  Shipping Notification
                </button>
                <button
                  className="flex w-full px-3 py-2 text-sm text-stone-700 hover:bg-stone-50"
                  onClick={() => { onResendEmail('review'); setShowEmailDropdown(false); }}
                >
                  Review Request
                </button>
              </div>
            )}
          </div>

          {canRefund && (
            <Button variant="danger" size="sm" onClick={onRefund}>
              Issue Refund
            </Button>
          )}
        </div>
      </div>
    </Card>
  );
}
